import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

const STATUS_CONFIG = {
  booked:           { label: 'BOOKED',           color: '#888888' },
  out_for_delivery: { label: 'OUT FOR DELIVERY', color: '#1d4ed8' },
  in_trial:         { label: 'IN TRIAL',         color: '#9c7d4f' },
  paid:             { label: 'PAID ✓',           color: '#16a34a' },
  completed:        { label: 'COMPLETED',        color: '#1a1a1a' },
};

export default function MyOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerPhone, setCustomerPhone] = useState(
    localStorage.getItem('fivetryCustomerPhone') || ''
  );
  const [phoneInput, setPhoneInput] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchOrders = async (phone) => {
    if (!phone) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const q = query(
        collection(db, 'bookings'),
        where('customerPhone', '==', phone)
      );
      const snapshot = await getDocs(q);
      const data = [];
      snapshot.forEach(doc => {
        data.push({ id: doc.id, ...doc.data() });
      });
      // Newest first
      data.sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return bTime - aTime;
      });
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders(customerPhone);
  }, [customerPhone]);

  const handlePhoneLookup = () => {
    const cleaned = phoneInput.replace(/\D/g, '');
    if (cleaned.length !== 10) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }
    localStorage.setItem('fivetryCustomerPhone', cleaned);
    setCustomerPhone(cleaned);
  };

  const handleChangePhone = () => {
    localStorage.removeItem('fivetryCustomerPhone');
    setCustomerPhone('');
    setOrders([]);
    setPhoneInput('');
  };

  // Shared FIVETRY navbar
  const navbar = (
    <div style={{ padding: '18px 24px', borderBottom: '0.5px solid #eeeae3', textAlign: 'center', background: '#fafaf7' }}>
      <h1 onClick={() => navigate('/')} style={{
        margin: 0, fontSize: '22px', fontWeight: '500', color: '#1a1a1a',
        fontFamily: '"Playfair Display", Georgia, serif',
        letterSpacing: '6px', cursor: 'pointer',
      }}>FIVETRY</h1>
    </div>
  );

  // ===== STATE 1: No phone stored — phone lookup =====
  if (!customerPhone) {
    return (
      <div style={{ minHeight: '100vh', background: '#fafaf7', fontFamily: 'Arial', paddingBottom: '90px' }}>
        {navbar}
        <div style={{ padding: '60px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: '13px', letterSpacing: '5px', color: '#9c7d4f', marginBottom: '14px', fontWeight: '600', textTransform: 'uppercase' }}>Find your orders</div>
          <h2 style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '28px', color: '#1a1a1a', margin: 0, marginBottom: '12px' }}>Look up by phone</h2>
          <p style={{ color: '#666', maxWidth: '320px', margin: '0 auto 24px', lineHeight: '1.6', fontSize: '14px' }}>
            Enter the phone number you used during your trial booking.
          </p>
          <input
            type="tel"
            placeholder="10-digit phone number"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePhoneLookup()}
            style={{
              width: '100%', maxWidth: '320px', padding: '14px 16px',
              fontSize: '15px', border: '1.5px solid #d6cdb8',
              borderRadius: '10px', outline: 'none', marginBottom: '14px',
              textAlign: 'center', letterSpacing: '1px',
            }}
          />
          <br />
          <button onClick={handlePhoneLookup} style={{
            background: '#1a1a1a', color: 'white', border: 'none',
            padding: '14px 32px', borderRadius: '10px', fontSize: '13px',
            letterSpacing: '2px', fontWeight: '600', cursor: 'pointer',
          }}>FIND MY ORDERS</button>
        </div>
      </div>
    );
  }

  // ===== STATE 2: Loading =====
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#fafaf7', fontFamily: 'Arial', paddingBottom: '90px' }}>
        {navbar}
        <div style={{ padding: '80px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: '12px', letterSpacing: '3px', color: '#9c7d4f', fontWeight: '600' }}>LOADING YOUR ORDERS...</div>
        </div>
      </div>
    );
  }

  // ===== STATE 3: No orders for this phone =====
  if (orders.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: '#fafaf7', fontFamily: 'Arial', paddingBottom: '90px' }}>
        {navbar}
        <div style={{ padding: '60px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: '13px', letterSpacing: '5px', color: '#9c7d4f', marginBottom: '14px', fontWeight: '600', textTransform: 'uppercase' }}>Fresh start</div>
          <h2 style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: '28px', color: '#1a1a1a', margin: 0, marginBottom: '12px' }}>No orders yet</h2>
          <p style={{ color: '#666', maxWidth: '320px', margin: '0 auto 24px', lineHeight: '1.6', fontSize: '14px' }}>
            We didn't find any orders for <strong>{customerPhone}</strong>. Place a trial booking and it'll appear here.
          </p>
          <button onClick={() => navigate('/')} style={{
            background: '#1a1a1a', color: 'white', border: 'none',
            padding: '14px 32px', borderRadius: '10px', fontSize: '13px',
            letterSpacing: '2px', fontWeight: '600', cursor: 'pointer',
            marginBottom: '14px',
          }}>SHOP NOW</button>
          <br />
          <button onClick={handleChangePhone} style={{
            background: 'transparent', color: '#9c7d4f', border: 'none',
            fontSize: '11px', letterSpacing: '2px', fontWeight: '600',
            cursor: 'pointer', textDecoration: 'underline', padding: '8px',
          }}>USE DIFFERENT PHONE</button>
        </div>
      </div>
    );
  }

  // ===== STATE 4: Orders list =====
  return (
    <div style={{ minHeight: '100vh', background: '#fafaf7', fontFamily: 'Arial', paddingBottom: '90px' }}>
      {navbar}

      {/* HEADER */}
      <div style={{ padding: '28px 20px 16px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '3px', color: '#9c7d4f', fontWeight: '700', marginBottom: '8px' }}>YOUR JOURNEY WITH US</div>
        <h2 style={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: '28px', fontWeight: '500', margin: 0, color: '#1a1a1a',
        }}>My Orders</h2>
        <p style={{ fontSize: '12px', color: '#888', marginTop: '6px', marginBottom: 0 }}>
          {orders.length} order{orders.length === 1 ? '' : 's'} · {customerPhone} · <span onClick={handleChangePhone} style={{ color: '#9c7d4f', cursor: 'pointer', textDecoration: 'underline' }}>change</span>
        </p>
      </div>

      {/* ORDER LIST */}
      <div style={{ padding: '4px 16px' }}>
        {orders.map((order) => {
          const status = STATUS_CONFIG[order.orderStatus || 'booked'];
          const isExpanded = expandedId === order.id;
          const isActive = order.orderStatus !== 'completed';

          return (
            <div key={order.id} style={{
              background: 'white',
              borderRadius: '14px',
              marginBottom: '14px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              border: isActive ? '1.5px solid #d6cdb8' : '1px solid #eeeae3',
              overflow: 'hidden',
            }}>
              {/* Card top — always visible */}
              <div
                onClick={() => setExpandedId(isExpanded ? null : order.id)}
                style={{ padding: '16px', cursor: 'pointer' }}
              >
                {/* Status pill */}
                <div style={{
                  display: 'inline-block',
                  background: status.color,
                  color: 'white',
                  padding: '5px 12px',
                  borderRadius: '999px',
                  fontSize: '10px',
                  letterSpacing: '1.5px',
                  fontWeight: '700',
                  marginBottom: '10px',
                }}>{status.label}</div>

                {/* Booking ID */}
                <div style={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontSize: '22px',
                  color: '#9c7d4f',
                  fontWeight: '600',
                  marginBottom: '10px',
                }}>{order.bookingId}</div>

                {/* Details */}
                <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.7' }}>
                  <div><span style={{ color: '#1a1a1a', fontWeight: '600' }}>Delivery:</span> {order.deliveryDay}</div>
                  <div><span style={{ color: '#1a1a1a', fontWeight: '600' }}>Slot:</span> {order.timeSlot}</div>
                  <div><span style={{ color: '#1a1a1a', fontWeight: '600' }}>Items:</span> {order.itemsSelected}</div>
                </div>

                {/* Expand hint */}
                <div style={{
                  marginTop: '12px',
                  fontSize: '11px',
                  letterSpacing: '1.5px',
                  color: '#9c7d4f',
                  fontWeight: '700',
                }}>
                  {isExpanded ? 'HIDE ITEMS ▴' : 'VIEW ITEMS ▾'}
                </div>
              </div>

              {/* Expanded items */}
              {isExpanded && order.selectedItems && (
                <div style={{
                  borderTop: '1px solid #eeeae3',
                  background: '#fafaf7',
                  padding: '14px 16px',
                }}>
                  {order.selectedItems.map((item, idx) => (
                    <div key={idx} style={{
                      display: 'flex',
                      gap: '12px',
                      padding: '10px 0',
                      borderBottom: idx < order.selectedItems.length - 1 ? '1px solid #eeeae3' : 'none',
                    }}>
                      <img src={item.image} alt={item.name} style={{
                        width: '60px', height: '80px', objectFit: 'cover', borderRadius: '6px', flexShrink: 0,
                      }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', color: '#1a1a1a', fontWeight: '600', marginBottom: '4px', lineHeight: '1.3' }}>{item.name}</div>
                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>
                          Size: <strong style={{ color: '#1a1a1a' }}>{item.size || 'N/A'}</strong>
                        </div>
                        <div style={{ fontSize: '13px', color: '#1a1a1a', fontWeight: '700' }}>{item.price}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}